<script lang="ts">
  import Home from './routes/Home.svelte';
  import PrivacyPolicy from './routes/PrivacyPolicy.svelte';
  import TermsOfService from './routes/TermsOfService.svelte';
  import { getPageMeta } from './utils/seo';
  import type { Component } from 'svelte';
  import type { CalculatorMeta } from './types/calculator';

  type MetaModule = { default: CalculatorMeta };
  const metaModules = import.meta.glob<MetaModule>('./calculators/*/meta.ts', { eager: true });
  const calculatorRoutes = (() => {
    const routes = new Map<string, () => Promise<{ default: Component }>>();

    for (const module of Object.values(metaModules)) {
      const route = module?.default?.route;
      const component = module?.default?.component as (() => Promise<{ default: Component }>) | undefined;
      if (!route || !component) continue;

      if (routes.has(route)) {
        console.warn(`[App] Skipping duplicate calculator route: ${route}`);
        continue;
      }

      routes.set(route, component);
    }

    return routes;
  })();

  let currentRoute = $state(window.location.pathname);
  let CurrentComponent = $state<Component | null>(null);

  $effect(() => {
    const loader = calculatorRoutes.get(currentRoute);
    if (loader) {
      loader().then((mod) => {
        CurrentComponent = mod.default;
        window.scrollTo(0, 0);
      });
    } else {
      CurrentComponent = null;
    }
  });

  $effect(() => {
    const { title, description } = getPageMeta(currentRoute);
    document.title = title;

    let metaDesc = document.querySelector<HTMLMetaElement>('meta[name="description"]');
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute('content', description);
  });

  $effect(() => {
    const handlePopState = () => {
      currentRoute = window.location.pathname;
    };
    window.addEventListener('popstate', handlePopState);

    const handleClick = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest('a');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href.startsWith('http') || href.startsWith('mailto:')) return;
      e.preventDefault();
      history.pushState(null, '', href);
      currentRoute = href;
    };
    document.addEventListener('click', handleClick);

    return () => {
      window.removeEventListener('popstate', handlePopState);
      document.removeEventListener('click', handleClick);
    };
  });
</script>

{#if calculatorRoutes.has(currentRoute) && CurrentComponent}
  <svelte:component this={CurrentComponent} />
{:else if currentRoute === '/privacy-policy'}
  <PrivacyPolicy />
{:else if currentRoute === '/terms-of-service'}
  <TermsOfService />
{:else}
  <Home />
{/if}
